"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { fullRows } from "@/lib/grid-rows";

export const REVEAL_STEP = 12;

type Options = {
  /** How many items are loaded now. */
  loaded: number;
  /** The server may hold more than is loaded (a limit-only endpoint asked again with a larger limit). */
  canFetchMore?: boolean;
  fetchMore?: () => void;
  isFetching?: boolean;
  /** Changes whenever the list is re-cut (tab, filter, query): the reveal starts over at twelve. */
  resetKey?: unknown;
  /** The grid's live column count (`useGridColumns`): `count` then fills whole rows. */
  columns?: number;
};

/**
 * TWELVE FIRST, THEN TWELVE MORE AS THE READER SCROLLS — /saved's rule.
 *
 * Loading more waits for the reader's own first scroll gesture (wheel, touch,
 * or a paging key) rather than a `scroll` event, which the page also fires on
 * its own when a shorter list makes the browser clamp the scroll position; a
 * screen tall enough to show the end of the grid still starts at twelve. It
 * first reveals what is already loaded, then asks the server for more.
 *
 * Its own observer, rebuilt whenever the visible count changes: revealing
 * loaded items fetches nothing, so the shared sentinel (which rebuilds when a
 * fetch lands) never heard of a sentinel that simply stayed on screen, and
 * stopped after one step. Rebuilt, it fires again while the end is still in
 * view, and stops once the end has moved past it.
 */
export function useScrollReveal({ loaded, canFetchMore = false, fetchMore, isFetching = false, resetKey, columns = 0 }: Options) {
  const [visible, setVisible] = useState(REVEAL_STEP);
  const [armed, setArmed] = useState(false);
  const [lastKey, setLastKey] = useState(resetKey);
  if (lastKey !== resetKey) {
    setLastKey(resetKey);
    setVisible(REVEAL_STEP);
  }

  useEffect(() => {
    if (armed) return;
    const arm = () => setArmed(true);
    const onKey = (e: KeyboardEvent) => { if (["ArrowDown", "PageDown", "End", " "].includes(e.key)) arm(); };
    window.addEventListener("wheel", arm, { passive: true });
    window.addEventListener("touchmove", arm, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("wheel", arm); window.removeEventListener("touchmove", arm); window.removeEventListener("keydown", onKey); };
  }, [armed]);

  const hidden = loaded > visible;
  const hasMore = hidden || canFetchMore;
  const more = () => {
    if (hidden) setVisible((n) => n + REVEAL_STEP);
    else if (canFetchMore && fetchMore) { fetchMore(); setVisible((n) => n + REVEAL_STEP); }
  };
  const moreRef = useRef(more);
  useEffect(() => { moreRef.current = more; });
  const nodeRef = useRef<HTMLElement | null>(null);
  const sentinel = useCallback((node: HTMLElement | null) => { nodeRef.current = node; }, []);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node || !armed || !hasMore || isFetching || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) moreRef.current(); }, { rootMargin: "400px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [armed, hasMore, isFetching, visible, loaded]);

  // What the grid shows: the revealed count cut to whole rows (lib/grid-rows).
  // The cut-off remainder is still "hidden", so the sentinel keeps loading.
  const count = fullRows(visible, loaded, columns, !canFetchMore);

  return { visible, count, hasMore, more, sentinel };
}
